import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Field, Form, withFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  makeStyles,
  InputLabel,
  IconButton,
  Button,
  Switch,
  MenuItem,
  Tooltip,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import { getErrorLabel, hasError } from "../../utils/forms/inputs";
import { buildApproverOption, hasAssignedTeams, selectableApprovers } from "../../utils/forms/approvers";
import Datetime from "react-datetime";
import moment from "moment";
import ElementsSelectTable from "../../tables/ProcessRelationship/ElementsSelectTable";
import ImageUploading from "react-images-uploading";
import Cancel from "@material-ui/icons/Cancel";
import ButtonIcon from "components/styled/ButtonIcon";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import Select from "react-select";
import history from "../../utils/history";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CancelIcon from "@material-ui/icons/Cancel";
import CachedIcon from "@material-ui/icons/Cached";
import { InfoOutlined } from "@material-ui/icons";
import RejectionListComponent from "./RejectionListComponent";
import LinksAttachmentsSection from "./LinksAttachmentsSection";
import { useApolloClient, useQuery } from "@apollo/react-hooks";
import { ADMINS_WITH_TEAMS_SEARCH, TEAMS_BY_APP } from "../gql/Queries";
import useGlobal from "../../store";

const RequisitionSchema = yup.object().shape({
  name: yup.string().required(),
  areaName: yup.string().required(),
  expirationDate: yup.mixed().required().test(
    'is-after-start',
    'La fecha de expiración debe ser mayor que la fecha de inicio',
    function(value) {
      const { scheduledSwitch, startDate } = this.parent;
      if (!scheduledSwitch || !startDate || !value) return true;
      return moment(value).isAfter(moment(startDate));
    }
  ),
  description: yup.string(),
  elementsIds: yup.array().required(),
  elementsValues: yup.array().required(),
  solutionType: yup.string().required(),
  solutionModuleId: yup.string(),
  questionEngineName: yup.string(),
  images: yup.array(),
  expiredSwitch: yup.boolean(),
  scheduledSwitch: yup.boolean(),
  startDate: yup.string().when('scheduledSwitch', {
    is: true,
    then: yup.string().required(),
    otherwise: yup.string().nullable(),
  }),
  reminders: yup.array(),
  notificationHour: yup.string(),
  externalEmails: yup.array(),
  approvalNotificationUsers: yup.array(),
  approvalNotificationByTeam: yup.boolean(),
  rejectionList: yup.array(),
  whatsappAiContext: yup.string(),
  attachments: yup.array(),
  links: yup.array(),
});

// Hook personalizado para debounce
const useDebounce = (callback, delay) => {
  const debounceRef = useRef();

  const debouncedCallback = useCallback(
    (...args) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

// Componente Field personalizado con debounce mejorado
const DebouncedTextField = memo(({ field, form, debounceDelay = 800, ...props }) => {
  const [localValue, setLocalValue] = useState(field.value);
  const [isFocused, setIsFocused] = useState(false);

  const debouncedSetFieldValue = useDebounce(
    useCallback((value) => {
      form.setFieldValue(field.name, value);
    }, [form.setFieldValue, field.name]),
    debounceDelay
  );

  const handleChange = useCallback((event) => {
    const value = event.target.value;
    setLocalValue(value);
    debouncedSetFieldValue(value);
  }, [debouncedSetFieldValue]);

  const handleFocus = useCallback((event) => {
    setIsFocused(true);
    if (field.onFocus) {
      field.onFocus(event);
    }
  }, [field]);

  const handleBlur = useCallback((event) => {
    setIsFocused(false);
    // Asegurar que se guarde el valor final al salir del campo
    form.setFieldValue(field.name, localValue);
    if (field.onBlur) {
      field.onBlur(event);
    }
  }, [field, form, localValue]);

  useEffect(() => {
    setLocalValue(field.value);
  }, [field.value]);

  return (
    <TextField
      {...props}
      id={field.name}
      name={field.name}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      autoComplete="off"
      autoFocus={false}
    />
  );
});

DebouncedTextField.displayName = 'DebouncedTextField';

const RequisitionForm = memo((props) => {
  const {
    errors,
    touched,
    values,
    setFieldValue,
    submitForm,
    isValid,
    elemets,
    questionEngineModules,
    disabled,
    editAction,
  } = props;

  const [questionEngineType, setQuestionEngineType] = useState(
    values.solutionType ? 1 : null
  );
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);

  const [session] = useGlobal(
    (state) => state.session,
    (actions) => actions
  );

  const [approverSearch, setApproverSearch] = useState({
    app_access: session.user ? session.appRef._id : "",
    email_contains: "",
  });
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [loadingAllApprovers, setLoadingAllApprovers] = useState(false);
  const apolloClient = useApolloClient();

  const { data: dataUsersGH, loading: loadingUsersGH } = useQuery(
    ADMINS_WITH_TEAMS_SEARCH,
    {
      variables: {
        where: approverSearch,
      },
    }
  );

  // Los aprobadores guardados se consultan por id porque la búsqueda por correo
  // no garantiza que aparezcan entre sus resultados.
  const { data: dataSavedApprovers } = useQuery(
    ADMINS_WITH_TEAMS_SEARCH,
    {
      variables: {
        where: {
          app_access: approverSearch.app_access,
          _id_in: values.approvalNotificationUsers ?? [],
        },
      },
      skip: !values.approvalNotificationUsers?.length || selectedApprovers.length > 0,
    }
  );

  const { data: dataTeams } = useQuery(TEAMS_BY_APP, {
    variables: {
      where: {
        app: approverSearch.app_access,
        archived: false,
      },
    },
    skip: !approverSearch.app_access,
  });

  const classes = useStyles();
  const { t } = useTranslation("processRelationship", "locations");

  // Memoizar opciones
  const questionEngineOptions = useMemo(
    () =>
      questionEngineModules?.map((module) => ({
        value: module._id,
        label: module.module.name,
      })) || [],
    [questionEngineModules]
  );

  const reminderOptions = useMemo(
    () => [
      { value: "", label: t("processRelationship.reminders.noReminder") },
      { value: "sameDay", label: t("processRelationship.reminders.sameDay") },
      {
        value: "1DayBefore",
        label: t("processRelationship.reminders.1DayBefore"),
      },
      {
        value: "3DaysBefore",
        label: t("processRelationship.reminders.3DaysBefore"),
      },
      {
        value: "5DaysBefore",
        label: t("processRelationship.reminders.5DaysBefore"),
      },
      {
        value: "7DaysBefore",
        label: t("processRelationship.reminders.7DaysBefore"),
      },
      {
        value: "10DaysBefore",
        label: t("processRelationship.reminders.10DaysBefore"),
      },
    ],
    [t]
  );

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  // Funciones memoizadas con useCallback
  const changedSwitchEnable = useCallback((event) => {
    setFieldValue("expiredSwitch", event.target.checked);
  }, [setFieldValue]);

  const changedScheduledSwitch = useCallback((event) => {
    setFieldValue("scheduledSwitch", event.target.checked);
    if (!event.target.checked) {
      setFieldValue("startDate", "");
    }
  }, [setFieldValue]);

  const handleStartDateChange = useCallback((e) => {
    if (e === "") {
      setFieldValue("startDate", "");
      return;
    }
    if (!moment.isMoment(e)) {
      setFieldValue("startDate", e);
      return;
    }
    const selected = e.clone();
    const previous = values.startDate ? moment(values.startDate) : null;
    if (!previous || !selected.isSame(previous, "day")) {
      selected.set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
    }
    setFieldValue("startDate", selected.toDate());
  }, [setFieldValue, values.startDate]);

  const handleChange = useCallback((reminder) => {
    setFieldValue("reminders", reminder.value ? [reminder.value] : []);
    if (!reminder.value) {
      setFieldValue("notificationHour", "");
    }
    setShowTimePicker(reminder.value !== "");
  }, [setFieldValue]);

  const changeHour = useCallback((event) => {
    setFieldValue("notificationHour", event.target.value);
  }, [setFieldValue]);

  const handleRadioChange = useCallback((event) => {
    const value = parseInt(event.target.value);
    setQuestionEngineType(value);
    setFieldValue("solutionType", value);
  }, [setFieldValue]);

  const handleDateChange = useCallback((e) => {
    if (e === "") {
      setFieldValue("expirationDate", "");
      return;
    }
    if (!moment.isMoment(e)) {
      setFieldValue("expirationDate", e);
      return;
    }
    const selected = e.clone();
    const previous = values.expirationDate ? moment(values.expirationDate) : null;
    if (!previous || !selected.isSame(previous, "day")) {
      selected.set({ hour: 23, minute: 59, second: 0, millisecond: 0 });
    }
    setFieldValue("expirationDate", selected.toDate());
  }, [setFieldValue, values.expirationDate]);

  const handleQuestionEngineSelect = useCallback((item) => {
    setFieldValue("solutionModuleId", item.value);
  }, [setFieldValue]);

  const teamNamesById = useMemo(
    () =>
      (dataTeams?.getTeamsByApp ?? []).reduce((acc, team) => {
        acc[team._id] = team.name;
        return acc;
      }, {}),
    [dataTeams]
  );

  const toApproverOption = useCallback(
    (user) => buildApproverOption(user, approverSearch.app_access, teamNamesById),
    [approverSearch.app_access, teamNamesById]
  );

  const approverOptions = useMemo(
    () =>
      selectableApprovers(dataUsersGH?.userGHs, approverSearch.app_access, values.approvalNotificationByTeam)
        .map(toApproverOption),
    [dataUsersGH, toApproverOption, values.approvalNotificationByTeam, approverSearch.app_access]
  );

  // La selección guarda usuarios para recalcular la etiqueta cuando llegan los nombres de equipos.
  const selectedApproverOptions = useMemo(
    () => selectedApprovers.map(toApproverOption),
    [selectedApprovers, toApproverOption]
  );

  useEffect(() => {
    const savedApprovers = dataSavedApprovers?.userGHs ?? [];
    if (!selectedApprovers.length && savedApprovers.length) {
      setSelectedApprovers(savedApprovers);
    }
  }, [dataSavedApprovers, selectedApprovers.length]);

  const handleApproverSelect = useCallback((options) => {
    const selection = options ?? [];
    setSelectedApprovers(selection.map((option) => option.user));
    setFieldValue("approvalNotificationUsers", selection.map((option) => option.value));
  }, [setFieldValue]);

  // Se consulta sin el texto de búsqueda para que "todos" no dependa de lo escrito en el selector.
  const handleSelectAllApprovers = useCallback(async () => {
    setLoadingAllApprovers(true);
    try {
      const { data } = await apolloClient.query({
        query: ADMINS_WITH_TEAMS_SEARCH,
        variables: { where: { app_access: approverSearch.app_access } },
        fetchPolicy: "network-only",
      });
      const approvers = selectableApprovers(
        data?.userGHs,
        approverSearch.app_access,
        values.approvalNotificationByTeam
      );
      setSelectedApprovers(approvers);
      setFieldValue("approvalNotificationUsers", approvers.map((user) => user._id));
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingAllApprovers(false);
    }
  }, [apolloClient, approverSearch.app_access, values.approvalNotificationByTeam, setFieldValue]);

  const changedApprovalNotificationByTeam = useCallback((event) => {
    const checked = event.target.checked;
    setFieldValue("approvalNotificationByTeam", checked);
    if (checked) {
      const approversWithTeams = selectedApprovers.filter((user) =>
        hasAssignedTeams(user, approverSearch.app_access)
      );
      setSelectedApprovers(approversWithTeams);
      setFieldValue("approvalNotificationUsers", approversWithTeams.map((user) => user._id));
    }
  }, [setFieldValue, selectedApprovers, approverSearch.app_access]);

  const handleApproverSearch = useCallback((searchValue) => {
    setApproverSearch((currentSearch) => ({
      ...currentSearch,
      email_contains: searchValue,
    }));
  }, []);

  const addEmail = (email) => {
    if (!email || !emailRegex.test(email)) return;
    if (values.externalEmails.includes(email)) return;

    setFieldValue("externalEmails", [...values.externalEmails, email])
    setInputValue('');
  };

  const handleKeyDown = useCallback((event) => {
    const key = event.key;
    if (['Enter', 'Tab', ',', ' '].includes(key)) {
      event.preventDefault();
      addEmail(event.target.value.trim());
    }

    if (key === 'Backspace' && event.target.value === '') {
      setFieldValue("externalEmails", values.externalEmails.slice(0, -1))
    }
  });

  const handleDelete = (emailToDelete) => {
    const updatedEmails = values.externalEmails.filter(email => email !== emailToDelete);
    setFieldValue("externalEmails", updatedEmails)
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[\s,;]+/);

    pastedEmails.forEach(email => {
      const trimmedEmail = email.trim();
      if (trimmedEmail && emailRegex.test(trimmedEmail)) {
        addEmail(trimmedEmail);
      }
    });
  };



  // useEffect optimizado
  useEffect(() => {
    const { elementsValues, elementsIds, reminders } = values;

    if (!elementsValues?.length && elementsIds?.length) {
      const allElementsValues = elemets?.map((element) => ({
        _id: element._id,
        value: element.name,
      })) || [];
      setFieldValue("elementsValues", allElementsValues);
    }

    setShowTimePicker(reminders?.length > 0 && reminders[0] !== "");
  }, [values.elementsIds.length, values.elementsValues.length, values.reminders, elemets, setFieldValue]);

  // Componente del motor de preguntas memoizado
  const questionEngineTypeSolution = useMemo(() => {
    switch (questionEngineType) {
      case 1:
        return (
          <FormControl fullWidth={true}>
            <h4>
              Selecciona un motor de preguntas para resolver la requisición
            </h4>
            <Select
              value={
                questionEngineOptions.find(
                  (option) => values.solutionModuleId === option.value
                ) || null
              }
              onChange={handleQuestionEngineSelect}
              options={questionEngineOptions}
              menuShouldScrollIntoView={false}
              menuPortalTarget={document.body}
            />
          </FormControl>
        );
      case 2:
        return (
          <Field
            name="questionEngineName"
            render={({ field, form }) => (
              <DebouncedTextField
                field={field}
                form={form}
                label={"Nombre del motor de preguntas para resolución"}
                success={
                  !hasError(errors, "questionEngineName") &&
                  touched.questionEngineName
                }
                error={
                  hasError(errors, "questionEngineName") &&
                  touched.questionEngineName
                }
                margin="normal"
                type={"text"}
                fullWidth
                debounceDelay={800}
              />
            )}
          />
        );
      default:
        return null;
    }
  }, [
    questionEngineType,
    questionEngineOptions,
    values.solutionModuleId,
    errors,
    touched,
    handleQuestionEngineSelect,
  ]);

  // Chips memoizados sin dependencias complejas
  const renderedChips = useMemo(() => {
    if (!values?.externalEmails?.length) return [];

    return values.externalEmails.map((email) => (
      <Chip
        key={email}
        label={email}
        onDelete={() => handleDelete(email)}
        color="primary"
        size="small"
        style={{ margin: "2px" }}
      />
    ));
  }, [values?.externalEmails?.join(','), setFieldValue]);

  // Componente de email simplificado con useMemo
  const EmailInputComponent = useMemo(() => memo(({ inputRef, ...props }) => (
    <TextField
      {...props}
      inputRef={inputRef}
      fullWidth
      variant="outlined"
      onFocus={() => setFocused(true)}
      onBlur={(e) => { addEmail(e.target.value); setFocused(false)}}
      onPaste={handlePaste}
      InputLabelProps={{
        zIndex: -1
      }}
      InputProps={{
        startAdornment: (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '4px',
            maxWidth: '100%',
            margin: '2px 0'
          }}>
            {renderedChips}
          </div>
        ),
        style: {
          alignItems: 'flex-start',
          paddingTop: '8px',
          flexWrap: 'wrap',
        },
      }}
      helperText={focused ? "Presione Enter, Tab o coma para agregar emails" : " "}
    />
  )), [renderedChips, focused]);

  return (
    <Form>
      <GridContainer justify="center">
        <GridItem xs={12} md={12}>
          <GridContainer>
            <GridItem xs={12}>
              <Accordion key={"main-info"} defaultExpanded={false}>
                <AccordionSummary>
                  <h4>Información Principal</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Field
                        name="name"
                        render={({ field, form }) => (
                          <DebouncedTextField
                            field={field}
                            form={form}
                            label={"Nombre"}
                            success={
                              !hasError(errors, "name") && touched.name
                            }
                            error={hasError(errors, "name") && touched.name}
                            margin="normal"
                            type={"text"}
                            fullWidth
                            debounceDelay={600}
                          />
                        )}
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <Field
                        name="description"
                        render={({ field, form }) => (
                          <DebouncedTextField
                            field={field}
                            form={form}
                            label={"Descripción"}
                            type={"text"}
                            fullWidth
                            multiline
                            rows={3}
                            success={
                              !hasError(errors, "description") &&
                              touched.description
                            }
                            error={hasError(errors, "description")}
                            debounceDelay={600}
                          />
                        )}
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <Field
                        name="areaName"
                        render={({ field, form }) => (
                          <DebouncedTextField
                            field={field}
                            form={form}
                            label={"Area que lo emite"}
                            success={
                              !hasError(errors, "areaName") &&
                              touched.areaName
                            }
                            error={
                              hasError(errors, "areaName") && touched.areaName
                            }
                            margin="normal"
                            type={"text"}
                            fullWidth
                            debounceDelay={600}
                          />
                        )}
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        fullWidth
                        component="fieldset"
                        className={classes.marginTop}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.scheduledSwitch}
                              onChange={changedScheduledSwitch}
                              name="scheduledSwitch"
                              classes={{
                                root: classes.root,
                                switchBase: classes.switchBase,
                                thumb: classes.thumb,
                                track: classes.track,
                                checked: classes.checked,
                              }}
                            />
                          }
                          label="Calendarizar"
                          className={classes.justifyContent}
                        />
                      </FormControl>
                    </GridItem>
                    {values.scheduledSwitch && (
                      <GridItem xs={6} style={{ marginTop: "30px" }}>
                        <InputLabel style={{ fontsize: "20px" }}>
                          Fecha inicio
                        </InputLabel>
                        <Datetime
                          name={"startDate"}
                          timeFormat={"HH:mm"}
                          value={values.startDate ? moment(values.startDate) : ""}
                          onChange={handleStartDateChange}
                          className={{ fontsize: "40px" }}
                        />
                      </GridItem>
                    )}
                    <GridItem xs={values.scheduledSwitch ? 6 : 12} style={{ marginTop: "30px" }}>
                      <InputLabel style={{ fontsize: "20px" }}>
                        Fecha expiración
                      </InputLabel>
                      <Datetime
                        name={"expirationDate"}
                        timeFormat={"HH:mm"}
                        value={moment(values.expirationDate)}
                        onChange={handleDateChange}
                        className={{ fontsize: "40px" }}
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        fullWidth
                        component="fieldset"
                        className={classes.marginTop}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.expiredSwitch}
                              onChange={changedSwitchEnable}
                              name="expiredSwitch"
                              classes={{
                                root: classes.root,
                                switchBase: classes.switchBase,
                                thumb: classes.thumb,
                                track: classes.track,
                                checked: classes.checked,
                              }}
                            />
                          }
                          label="No permitir respuestas después de la expiración"
                          className={classes.justifyContent}
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        component="fieldset"
                        className={classes.marginTop}
                      >
                        <RadioGroup
                          row
                          aria-label="position"
                          name="position"
                          value={values.solutionType.toString()}
                          onChange={handleRadioChange}
                        >
                          <FormControlLabel
                            value="1"
                            className={classes.textColor}
                            control={
                              <Radio
                                color="default"
                                classes={{
                                  root: classes.radio,
                                  checked: classes.checked,
                                }}
                              />
                            }
                            label={
                              "Selecciona un motor de preguntas para resolver la requisición"
                            }
                          />
                          <FormControlLabel
                            value="2"
                            className={classes.textColor}
                            control={
                              <Radio
                                color="default"
                                classes={{
                                  root: classes.radio,
                                  checked: classes.checked,
                                }}
                              />
                            }
                            label={
                              "Crear un motor de preguntas para resolver la requisición"
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                      {questionEngineTypeSolution}
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
          </GridContainer>

          <GridContainer style={{ marginTop: "10px" }}>
            <GridItem xs={12}>
              <Accordion key={"whatsapp-ai-info"}>
                <AccordionSummary>
                  <h4>Configuración del Agente de IA WhatsApp</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Field
                        name="whatsappAiContext"
                        render={({ field, form }) => (
                          <DebouncedTextField
                            field={field}
                            form={form}
                            label={"Contexto para la IA de los grupos"}
                            placeholder={"Contexto para la IA de los grupos"}
                            multiline
                            rows={4}
                            type={"text"}
                            fullWidth
                            success={
                              !hasError(errors, "whatsappAiContext") &&
                              touched.whatsappAiContext
                            }
                            error={hasError(errors, "whatsappAiContext")}
                            debounceDelay={800}
                          />
                        )}
                      />
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
          </GridContainer>

          <GridContainer style={{ marginTop: "10px" }}>
            <GridItem xs={12} md={12} lg={12}>
              <Accordion key={"notifications-info"}>
                <AccordionSummary>
                  <h4>Notificaciones</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={6}>
                      <FormControl fullWidth>
                        <label>
                          {t("processRelationship.reminders.selectLabel")}
                        </label>
                        <Select
                          value={
                            values.reminders.length > 0
                              ? reminderOptions.find(
                                  (option) =>
                                    values.reminders[0] === option.value
                                )
                              : reminderOptions[0]
                          }
                          labelId="simple-select-label"
                          id="simple-select"
                          label={t("processRelationship.reminders.selectLabel")}
                          options={reminderOptions}
                          onChange={handleChange}
                          menuShouldScrollIntoView={false}
                          menuPortalTarget={document.body}
                        />
                      </FormControl>
                    </GridItem>
                    {showTimePicker && (
                      <>
                        <GridItem xs={5}>
                          <form className={classes.container} noValidate>
                            <TextField
                              onChange={changeHour}
                              id="time"
                              label={t(
                                "processRelationship.reminders.timePickerLabel"
                              )}
                              type="time"
                              value={values.notificationHour}
                              className={classes.timePicker}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 300,
                              }}
                            />
                          </form>
                        </GridItem>
                        <Tooltip
                          size={10}
                          className={classes.tooltipInfo}
                          id="tooltip-top"
                          title={t(
                            "processRelationship.reminders.timeReminderInfo"
                          )}
                          placement="top"
                        >
                          <InfoOutlined />
                        </Tooltip>
                      </>
                    )}
                    <GridItem xs={12} style={{ marginTop: "10px" }}>
                      <label>
                        {t("processRelationship.externalNotification")}
                      </label>
                       <EmailInputComponent
                    inputRef={input => {
                      if (input && focused) {
                        input.focus();
                      }
                    }}
                    defaultValue={inputValue}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                    </GridItem>
                    <GridItem xs={12} style={{ marginTop: "10px" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.approvalNotificationByTeam}
                            onChange={changedApprovalNotificationByTeam}
                            name="approvalNotificationByTeam"
                            classes={{
                              root: classes.root,
                              switchBase: classes.switchBase,
                              thumb: classes.thumb,
                              track: classes.track,
                              checked: classes.checked,
                            }}
                          />
                        }
                        label="Notificar a cada administrador solo las requisiciones de usuarios de su equipo"
                        className={classes.justifyContent}
                      />
                      <Tooltip
                        className={classes.tooltipInfo}
                        classes={{ tooltip: classes.approvalTooltip }}
                        title={
                          <>
                            <p>Cada administrador recibe solo las requisiciones por aprobar de los usuarios de su equipo.</p>
                            <p>Solo se pueden elegir administradores con equipo asignado.</p>
                            <p>Si el usuario no pertenece al equipo de ningún administrador seleccionado, no se envía correo.</p>
                          </>
                        }
                        placement="top"
                      >
                        <InfoOutlined />
                      </Tooltip>
                    </GridItem>
                    <GridItem xs={12} style={{ marginTop: "10px" }}>
                      <div className={classes.approversHeader}>
                        <label>
                          Administradores a notificar cuando haya requisiciones por aprobar
                          {selectedApprovers.length > 0 && ` (${selectedApprovers.length} seleccionados)`}
                        </label>
                        <Button
                          size="small"
                          color="primary"
                          onClick={handleSelectAllApprovers}
                          disabled={loadingAllApprovers || !approverSearch.app_access}
                        >
                          {loadingAllApprovers ? "Cargando..." : "Seleccionar todos"}
                        </Button>
                      </div>
                      <Select
                        styles={{
                          valueContainer: (base) => ({ ...base, maxHeight: "160px", overflowY: "auto" }),
                        }}
                        value={selectedApproverOptions}
                        onChange={handleApproverSelect}
                        onInputChange={handleApproverSearch}
                        options={approverOptions}
                        isMulti
                        isClearable
                        isLoading={loadingUsersGH}
                        placeholder={"Buscar administradores por correo"}
                        noOptionsMessage={() =>
                          values.approvalNotificationByTeam
                            ? "Sin administradores con equipo asignado"
                            : "Sin resultados"
                        }
                        menuShouldScrollIntoView={false}
                        menuPortalTarget={document.body}
                      />
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
          </GridContainer>
        </GridItem>
        <GridItem xs={12} md={12}>
          <GridContainer style={{ marginTop: "10px" }}>
            <GridItem xs={12}>
              <Accordion key={"elements-info"}>
                <AccordionSummary>
                  <h4>Elementos Asociados</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Card className={classes.heightImages}>
                        <ElementsSelectTable
                          data={elemets}
                          setFieldValue={setFieldValue}
                          elementsIds={values.elementsIds}
                          elementsValues={values.elementsValues}
                          disabled={disabled}
                          editAction={editAction}
                        />
                      </Card>
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
            <GridItem xs={12}>
              <Accordion
                key={"rejection-list-info"}
                style={{ marginTop: "10px" }}
                // defaultExpanded={true}
              >
                <AccordionSummary>
                  <h4>Lista de Rechazo de Elementos</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={12}>
                      <RejectionListComponent
                        rejectionList={values.rejectionList || []}
                        setFieldValue={setFieldValue}
                      />
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
            <GridItem xs={12}>
              <Accordion key={"images-info"} style={{ marginTop: "10px" }}>
                <AccordionSummary>
                  <h4>Imágenes Adjuntas</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Card>
                        <CardBody className={classes.heightImages}>
                          <Field
                            name={"images"}
                            render={({ field }) => (
                              <ImageUploading
                                multiple
                                value={field.value}
                                onChange={(imageList, addUpdateIndex) => {
                                  setFieldValue("images", imageList);
                                }}
                                dataURLKey="url"
                              >
                                {({
                                  imageList,
                                  onImageUpload,
                                  onImageRemoveAll,
                                  onImageUpdate,
                                  onImageRemove,
                                  isDragging,
                                  dragProps,
                                }) => (
                                  <GridContainer>
                                    <GridItem xs={12}>
                                      <GridContainer>
                                        <GridItem xs={6}>
                                          <Button
                                            variant="contained"
                                            size="small"
                                            style={
                                              isDragging
                                                ? { color: "red" }
                                                : {
                                                    backgroundColor: "#6ab3d4",
                                                    color: "#fff",
                                                  }
                                            }
                                            onClick={onImageUpload}
                                            {...dragProps}
                                          >
                                            Subir Imagenes
                                          </Button>
                                        </GridItem>
                                        <GridItem xs={6}>
                                          <Button
                                            color="secondary"
                                            size="small"
                                            variant="contained"
                                            onClick={onImageRemoveAll}
                                          >
                                            Remover Todas
                                          </Button>
                                        </GridItem>
                                      </GridContainer>
                                    </GridItem>
                                    <GridItem xs={12}>
                                      <div
                                        className={classes.imageItemBtnWrapper}
                                      >
                                        {imageList.map((image, index) => (
                                          <div
                                            key={index}
                                            className={classes.imageItem}
                                          >
                                            <img
                                              src={image.url}
                                              alt=""
                                              width="150"
                                            />
                                            <div
                                              className={
                                                classes.imageItemBtnWrapper
                                              }
                                            >
                                              <IconButton
                                                onClick={() =>
                                                  onImageUpdate(index)
                                                }
                                                style={{ color: "#6ab3d4" }}
                                                aria-label="Actualizar"
                                                component="span"
                                              >
                                                <CachedIcon />
                                              </IconButton>
                                              <IconButton
                                                onClick={() =>
                                                  onImageRemove(index)
                                                }
                                                style={{ color: "red" }}
                                                aria-label="Eliminar"
                                                component="span"
                                              >
                                                <CancelIcon />
                                              </IconButton>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </GridItem>
                                  </GridContainer>
                                )}
                              </ImageUploading>
                            )}
                          />
                        </CardBody>
                      </Card>
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
            <GridItem xs={12}>
              <Accordion key={"links-attachments"} style={{ marginTop: "10px" }}>
                <AccordionSummary>
                  <h4>Links y Archivos Adjuntos</h4>
                </AccordionSummary>
                <AccordionDetails>
                  <GridContainer>
                    <GridItem xs={12}>
                      <LinksAttachmentsSection
                        attachments={values.attachments || []}
                        links={values.links || []}
                        setFieldValue={setFieldValue}
                      />
                    </GridItem>
                  </GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>
      <GridContainer justify="center" className={classes.marginTop}>
        <GridItem>
          <ButtonIcon
            onClick={() => history.goBack()}
            icon={Cancel}
            color={"primary"}
            label={"Cancelar"}
          />
        </GridItem>
        <GridItem>
          <ButtonIcon
            disabled={!isValid}
            icon={CheckCircleIcon}
            label={"Guardar"}
            color={"info"}
            block={true}
            onClick={submitForm}
          />
        </GridItem>
      </GridContainer>
    </Form>
  );
});

RequisitionForm.displayName = 'RequisitionForm';

const useStyles = makeStyles((theme) => ({
  marginTop: {
    marginTop: "15px",
  },
  textColor: {
    color: "#000",
  },
  error: {
    backgroundColor: "red",
  },
  radio: {
    "&$checked": {
      color: "#6ab3d4",
    },
  },
  checked: {},
  heightImages: {
    maxHeight: "350px",
    overflowY: "scroll",
  },
  imageItem: {
    display: "flex",
    margin: "15px 0",
  },
  imageItemBtnWrapper: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "10px",
    alignItems: "center",
  },
  selectButtons: {
    display: "flex",
    marginLeft: "10px",
  },
  timePicker: {
    paddingBottom: "3px",
  },
  tooltipInfo: {
    right: "0px",
    color: "gray",
    fontSize: "20px",
  },
  approversHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  approvalTooltip: {
    fontSize: "14px",
    lineHeight: 1.5,
    maxWidth: "380px",
    padding: "8px 12px",
    "& p": {
      margin: "4px 0",
    },
  },
}));

const RequisitionFormFK = withFormik({
  validationSchema: RequisitionSchema,
  validateOnChange: true,
  mapPropsToValues: ({ requisition }) => {
    return {
      name: requisition?.name ?? "",
      expirationDate: requisition?.expirationDate ?? moment().set({ hour: 23, minute: 59, second: 0, millisecond: 0 }).toDate(),
      areaName: requisition?.areaName ?? "",
      description: requisition?.description ?? "",
      solutionType: requisition?.solutionModuleId ? 1 : "",
      elementsIds: requisition?.elementsIds ?? [],
      elementsValues: requisition?.elementsValues ?? [],
      images: requisition?.images?.length
        ? requisition?.images?.map((image) => ({ url: image.original }))
        : [],
      questionEngineName: "",
      solutionModuleId: requisition?.solutionModuleId ?? "",
      expiredSwitch: requisition?.expiredSwitch ?? false,
      scheduledSwitch: !!(requisition?.startDate),
      startDate: requisition?.startDate ?? "",
      reminders: requisition?.reminders?.map((r) => r.split(":")[0]) ?? [],
      notificationHour: requisition?.notificationHour ?? "09:00",
      externalEmails: requisition?.externalEmails ?? [],
      approvalNotificationUsers: requisition?.approvalNotificationUsers ?? [],
      approvalNotificationByTeam: requisition?.approvalNotificationByTeam ?? false,
      rejectionList: requisition?.rejectionList ?? [],
      whatsappAiContext: requisition?.whatsappAiContext ?? "",
      attachments: requisition?.attachments?.length
        ? requisition.attachments.map((a) => ({ url: a.url, name: a.name, type: a.type }))
        : [],
      links: requisition?.links ?? [],
    };
  },
  handleSubmit: (values, { props }) => {

    const { rejectionList } = values;
    const filteredRejectionList = rejectionList.filter(
      (item) => item !== 'Otro'
    );

    values.rejectionList = filteredRejectionList;

    props.submitSuccess(values);
  },
})(RequisitionForm);

export default RequisitionFormFK;
